import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    Text,
    Input,
    InputGroup,
    InputRightElement,
    useToast,
    Alert,
    AlertIcon,
    Code,
    Box,
    Divider,
    Card,
    CardBody,
    useColorModeValue,
} from '@chakra-ui/react';
import { FiCopy, FiExternalLink, FiMail, FiShare2, FiUser } from 'react-icons/fi';
import Icon from 'components/icons/Icon';
import { agencyService, AgencyProfile } from '../../../../services/agencyService';

interface GenerateSignupLinkModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const GenerateSignupLinkModal: React.FC<GenerateSignupLinkModalProps> = ({
    isOpen,
    onClose,
}) => {
    const [agencyProfile, setAgencyProfile] = useState<AgencyProfile | null>(null);
    const [signupLink, setSignupLink] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const toast = useToast();

    const bgColor = useColorModeValue('gray.50', 'navy.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const modalBg = useColorModeValue('white', 'navy.800');
    const textColor = useColorModeValue('navy.700', 'white');
    const textColorSecondary = useColorModeValue('gray.600', 'gray.400');
    const inputBg = useColorModeValue('white', 'navy.700');
    const inputBorder = useColorModeValue('gray.200', 'gray.600');
    const codeBg = useColorModeValue('gray.100', 'navy.900');

    useEffect(() => {
        if (isOpen) {
            loadAgencyProfile();
        }
    }, [isOpen]);

    const loadAgencyProfile = async () => {
        setIsLoading(true);
        try {
            const profile = await agencyService.getAgencyProfile();
            setAgencyProfile(profile);
            
            // Generate the signup link
            const link = agencyService.generateTrainerSignupLink(profile.name);
            setSignupLink(link);
        } catch (error: any) {
            console.error('Failed to load agency profile:', error);
            toast({
                title: 'Error',
                description: error.message || 'Failed to load agency profile',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = async () => {
        if (!signupLink) {
            toast({
                title: 'No Link Available',
                description: 'Please wait for the signup link to be generated',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            console.log('Attempting to copy:', signupLink);
            
            // Method 1: Try modern clipboard API
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(signupLink);
                console.log('Copied using clipboard API');
            } else {
                // Method 2: Try using the input field directly
                const inputElement = document.getElementById('signup-link-input') as HTMLInputElement;
                if (inputElement) {
                    inputElement.select();
                    inputElement.setSelectionRange(0, 99999); // For mobile devices
                    const successful = document.execCommand('copy');
                    if (!successful) {
                        throw new Error('execCommand failed');
                    }
                    console.log('Copied using input selection');
                } else {
                    // Method 3: Fallback with textarea
                    const textArea = document.createElement('textarea');
                    textArea.value = signupLink;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-999999px';
                    textArea.style.top = '-999999px';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();
                    
                    const successful = document.execCommand('copy');
                    document.body.removeChild(textArea);
                    
                    if (!successful) {
                        throw new Error('All copy methods failed');
                    }
                    console.log('Copied using textarea fallback');
                }
            }
            
            setCopied(true);
            toast({
                title: 'Link Copied!',
                description: 'The signup link has been copied to your clipboard',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            
            // Reset copied state after 3 seconds
            setTimeout(() => setCopied(false), 3000);
        } catch (error) {
            console.error('Copy failed:', error);
            
            // As a last resort, select the input text for manual copying
            const inputElement = document.getElementById('signup-link-input') as HTMLInputElement;
            if (inputElement) {
                inputElement.focus();
                inputElement.select();
            }
            
            toast({
                title: 'Copy Failed',
                description: 'Please manually copy the selected text (Ctrl+C or Cmd+C)',
                status: 'error',
                duration: 8000,
                isClosable: true,
            });
        }
    };

    const openSignupLink = () => {
        window.open(signupLink, '_blank');
    };

    const shareViaEmail = () => {
        const subject = encodeURIComponent(`Join ${agencyProfile?.name} as a Trainer`);
        const body = encodeURIComponent(
            `Hi there!\n\n` +
            `You've been invited to join ${agencyProfile?.name} as a trainer.\n\n` +
            `Please click the link below to create your trainer account:\n` +
            `${signupLink}\n\n` +
            `The agency information will be pre-filled for you - you just need to enter your personal details.\n\n` +
            `Best regards,\n` +
            `${agencyProfile?.name} Team`
        );
        
        const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
        window.location.href = mailtoLink;
    };

    const handleClose = () => {
        setCopied(false);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="xl">
            <ModalOverlay />
            <ModalContent bg={modalBg}>
                <ModalHeader>
                    <HStack spacing={3}>
                        <Icon as={FiShare2} color="blue.500" />
                        <Text color={textColor}>Generate Trainer Signup Link</Text>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton color={textColor} />
                <ModalBody>
                    {isLoading ? (
                        <VStack spacing={4} py={8}>
                            <Text color={textColor}>Loading agency information...</Text>
                        </VStack>
                    ) : (
                        <VStack spacing={6} align="stretch">
                            {/* Agency Info */}
                            {agencyProfile && (
                                <Card bg={bgColor} border="1px solid" borderColor={borderColor}>
                                    <CardBody>
                                        <VStack spacing={3} align="start">
                                            <HStack>
                                                <Icon as={FiUser} color="blue.500" />
                                                <Text fontWeight="600" fontSize="md" color={textColor}>
                                                    Agency: <Text as="span" color={useColorModeValue('blue.600', 'cyan.300')}>{agencyProfile.name}</Text>
                                                </Text>
                                            </HStack>
                                            <Text fontSize="sm" color={textColorSecondary}>
                                                Trainers who use this link will be automatically associated with your agency.
                                            </Text>
                                        </VStack>
                                    </CardBody>
                                </Card>
                            )}

                            {/* Instructions */}
                            <Alert status="info" borderRadius="md" bg={useColorModeValue('blue.50', 'blue.900')} borderColor={useColorModeValue('blue.200', 'blue.700')}>
                                <AlertIcon />
                                <VStack align="start" spacing={1}>
                                    <Text fontWeight="500" fontSize="sm" color={textColor}>
                                        How it works:
                                    </Text>
                                    <Text fontSize="sm" color={textColor}>
                                        1. Share this link with potential trainers<br/>
                                        2. They click the link and fill in their personal details<br/>
                                        3. Their account is automatically linked to your agency
                                    </Text>
                                </VStack>
                            </Alert>

                            {/* Signup Link */}
                            <Box>
                                <Text fontWeight="500" mb={3} color={textColor}>Trainer Signup Link:</Text>
                                <InputGroup>
                                    <Input
                                        id="signup-link-input"
                                        value={signupLink}
                                        readOnly
                                        bg={inputBg}
                                        borderColor={inputBorder}
                                        color={textColor}
                                        fontSize="sm"
                                        pr="120px"
                                        _focus={{ borderColor: 'blue.500', bg: inputBg }}
                                        onClick={(e) => e.currentTarget.select()}
                                    />
                                    <InputRightElement width="110px">
                                        <HStack spacing={1}>
                                            <Button
                                                size="xs"
                                                onClick={copyToClipboard}
                                                colorScheme={copied ? "green" : "blue"}
                                                variant="ghost"
                                                isDisabled={!signupLink}
                                            >
                                                <Icon as={FiCopy} />
                                            </Button>
                                            <Button
                                                size="xs"
                                                onClick={openSignupLink}
                                                colorScheme="blue"
                                                variant="ghost"
                                                isDisabled={!signupLink}
                                            >
                                                <Icon as={FiExternalLink} />
                                            </Button>
                                        </HStack>
                                    </InputRightElement>
                                </InputGroup>
                            </Box>

                            <Divider borderColor={borderColor} />

                            {/* Share Options */}
                            <Box>
                                <Text fontWeight="500" mb={3} color={textColor}>Share Options:</Text>
                                <HStack spacing={3}>
                                    <Button
                                        leftIcon={<Icon as={FiCopy} />}
                                        onClick={copyToClipboard}
                                        colorScheme={copied ? "green" : "blue"}
                                        variant="outline"
                                        size="sm"
                                    >
                                        {copied ? "Copied!" : "Copy Link"}
                                    </Button>
                                </HStack>
                            </Box>

                            {/* Example URL Display */}
                            <Box>
                                <Text fontWeight="500" mb={2} fontSize="sm" color={textColor}>URL Preview:</Text>
                                <Code fontSize="xs" p={2} borderRadius="md" w="full" wordBreak="break-all" bg={codeBg} color={textColor}>
                                    {signupLink}
                                </Code>
                            </Box>
                        </VStack>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button onClick={handleClose} color={textColor}>
                        Close
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default GenerateSignupLinkModal;